import React, { PropsWithChildren, useMemo } from 'react'
import cn from 'clsx'
import { connect } from 'react-redux'

import Tooltip from 'components/Luxkit/Tooltip'
import config from 'constants/config'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import SolidExclamationCircleIcon from 'components/Luxkit/Icons/solid/SolidExclamationCircleIcon'
import {
  Root,
  OfferType,
  CacheOffText, ValueWrapper,
} from '../styles'
import FormatCurrency from 'components/Common/FormatCurrency'
import { decodeSearchParams } from 'lib/search/searchUtils'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import Caption from 'components/Luxkit/Typography/Caption'
import styled from 'styled-components'
import { rem } from 'polished'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'

const LineText = styled(CaptionTextBlock)`
  margin-top: ${rem(6)};
`

interface Props {
  className?: string;
  durationLabel: string;
  flightCacheOff?: boolean;
  hasClause?: boolean;
  infoBar?: boolean;
  infoBarMedium?: boolean;
  infoBarMini?: boolean;
  isFlightPrice?: boolean;
  label: string;
  offerSaleUnit: string;
  price: number;
  taxesAndFees: number;
  roomOccupancy?: string;
  type: string;
  shouldShowValue?: boolean;
  showFlights?: boolean;
  value: number;
  originAirport?: App.Airport;
  noFlightsFromOrigin?: boolean;
  discountPercent: number;
  roomType?: App.PackageRoomType;
  searchLocation?: any;
  maxChildAge?: number;
  maxInfantAge?: number;
  roomPolicyDescription?: string;
  availablePackage?: App.Package;
  offer: App.Offer | App.OfferSummary;
}

function OfferPrice({
  durationLabel,
  hasClause,
  infoBar,
  label,
  offerSaleUnit,
  price,
  roomOccupancy,
  shouldShowValue,
  showFlights,
  flightCacheOff,
  isFlightPrice,
  taxesAndFees,
  value,
  type,
  discountPercent,
  children,
  originAirport,
  noFlightsFromOrigin,
  roomType,
  searchLocation,
  maxChildAge,
  maxInfantAge,
  roomPolicyDescription,
  availablePackage,
  offer,
}: PropsWithChildren<Props>) {
  const showIncludedGuestsExceeded = useMemo(() => {
    if (!config.SEARCH_ENABLED || !roomType || availablePackage?.roomRate?.extraGuestSurcharges.length > 0) {
      return false
    }

    const filters = decodeSearchParams(searchLocation, maxChildAge, maxInfantAge)
    return filters.rooms.some(room => hasIncludedGuestsExceeded(room, availablePackage, offer))
  }, [roomType, availablePackage, searchLocation, maxChildAge, maxInfantAge, offer])

  const hotelPrice = price

  return (
    <Root data-testid={`${isFlightPrice ? 'flights-' : ''}pricing`}>
      {showFlights && <OfferType vertical="center" size="xs">
        {isFlightPrice && <div><LinePlaneFlyIcon /></div>}
        <Caption variant="large" format="titlecase" weight="bold">{label}</Caption>
      </OfferType>}
      {isFlightPrice && flightCacheOff &&
        <CacheOffText
          className={cn({ short: !roomOccupancy, infobar: infoBar })}
          variant="medium"
          colour="neutral-three"
        >
          {noFlightsFromOrigin && originAirport &&
            `No flights available from ${originAirport.name}. Please select another airport`
          }
          {(!noFlightsFromOrigin || !originAirport) &&
            'Add flights to the hotel package. Select from all major airlines at the next step.'
          }
        </CacheOffText>
      }
      {(!isFlightPrice || !flightCacheOff) && <>
        {hotelPrice && <PriceRowPriceCaption>
          {durationLabel} from
        </PriceRowPriceCaption>}
        {hotelPrice && <PriceRowPrice
          size="L"
          price={Math.ceil(hotelPrice)}
          saleUnit={`${offerSaleUnit}${hasClause ? '*' : ''}`}
          taxesAndFees={taxesAndFees}
        />}
        <TaxesAndFees
          taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
          taxAmount={taxesAndFees}
        />
        {!isFlightPrice && roomOccupancy && (
          <Caption variant="medium" colour="neutral-three">
            {roomOccupancy}
          </Caption>
        )}
        {isFlightPrice &&
          <Caption variant="medium" colour="neutral-three">
            Two Adults
          </Caption>
        }
      </>}
      {children}
      {showIncludedGuestsExceeded && !(isFlightPrice && flightCacheOff) && (
        <AdditionalGuestsPopup
          complex={false}
          description="+ Extra guest surcharge"
          modalContent={roomPolicyDescription}
        />
      )}
      {shouldShowValue && value >= price &&
        <ValueWrapper variant="medium" weight="bold">
          <span
            data-testid="value-box">
            Valued up to <FormatCurrency value={Math.ceil(value)} />
          </span>
        </ValueWrapper>
      }
      {discountPercent > 0 && type === OFFER_TYPE_LAST_MINUTE &&
        <LineText
          variant="medium"
          weight="bold"
          colour="neutral-three"
          data-testid="value-box"
          endIcon={<Tooltip
            description="Discount calculated on the best rate available for this package as reported to us by the property. Rate excludes the value of any package inclusions – they’re our gift to you."
          >
            <SolidExclamationCircleIcon colour="primary" />
          </Tooltip>}
        >
          {discountPercent}% off best available rate
        </LineText>
      }
    </Root>
  )
}

function mapStateToProps(state: App.State) {
  return {
    searchLocation: state.router.location.search,
  }
}

export default connect(mapStateToProps)(OfferPrice)
